import * as React from 'react';

import {
  ApolloQueryResult,
  OperationVariables,
  QueryOptions,
  TypedDocumentNode,
  useApolloClient,
  ApolloError,
} from '@apollo/client';

export type MyLazyQueryResult<T> = Pick<
  ApolloQueryResult<T>,
  'error' | 'loading'
>;

// can't use useLazyQuery which would be much better due to https://github.com/apollographql/apollo-client/issues/9317
export default function useLazyQueryWorkaround<
  T,
  TVariables = OperationVariables
>(
  query: TypedDocumentNode<T, TVariables>,
  onCompleted: (
    data: T,
    queryOptions: Omit<QueryOptions<TVariables, T>, 'query'>
  ) => void
): [
  (
    additionalOptions: Omit<QueryOptions<TVariables, T>, 'query'>
  ) => Promise<void>,
  MyLazyQueryResult<T>
] {
  const onCompletedRef = React.useRef(onCompleted);

  React.useEffect(() => {
    onCompletedRef.current = onCompleted;
  });

  const client = useApolloClient();
  const self = React.useRef<undefined | Record<string, unknown>>(undefined);

  const [result, setResult] = React.useState<MyLazyQueryResult<T>>({
    loading: false,
  });

  const execQuery = React.useCallback(
    async (additionalOptions: Omit<QueryOptions<TVariables, T>, 'query'>) => {
      const current = {};
      self.current = current;
      try {
        setResult({
          loading: true,
          error: undefined,
        });
        const queryResult = await client.query({
          query,
          ...additionalOptions,
        });
        if (self.current !== current) {
          // query canceled
          return;
        }
        setResult({
          loading: false,
          error: queryResult.error,
        });

        onCompletedRef.current(queryResult.data, additionalOptions);
      } catch (error: unknown) {
        if (self.current !== current) {
          // query canceled
          return;
        }
        setResult({
          loading: false,
          error: error as ApolloError,
        });
      }
    },
    [client, query]
  );

  return [execQuery, result];
}
